import axios, { AxiosRequestConfig } from 'axios'
import { nextTick } from 'vue'
import type { MicroserviceError, IFetch, IRequestConfig } from '@types'
import { useUIStore } from '@/js/stores/uiStore'
import type { IRogue } from '@/js/services/RogueTypes'

function writeToCloudflareIframe(html: string) {
  const frameEl = document.getElementById('cloudflare-access-iframe') as HTMLIFrameElement
  if (frameEl) {
    const frame = frameEl.contentWindow?.document || frameEl.contentDocument
    if (frame) {
      frame.open()
      frame.write(html)
      frame.close()
    }
  }
}

export const axiosInstance: IFetch & { rogue?: IRogue } = {
  request<T>(config: IRequestConfig): Promise<T> {
    const { url, method, data } = config

    const axiosConfig = config as AxiosRequestConfig

    if (url.indexOf('/svc_session') === 0) {
      axiosConfig.withCredentials = true
    }
    axiosConfig.baseURL = this.rogue?.baseURL

    return axios.request<T>(axiosConfig)
      .then(resp => resp.data)
      .catch(e => {
        // Transform the axios error into a MicroserviceError

        // Debug object for logging, in case this is not parsable
        const debug = { message: e.message, data, method, url, body: '', json: {} }

        // Error object will be updated depending on the results
        let message = e.message
        let status = e.status || 500
        let error = ''

        const errStack = e.stack

        const debugUrl = url.split('?')[0]

        // Aborted requests (render timeout) - downgrade to debug so a single
        // wedge doesn't spam logs with one warning per in-flight request.
        if (axios.isCancel(e)) {
          this.rogue?.log.debug(`Request canceled: ${method || 'get'} to ${debugUrl}`)
          const err = new Error('canceled') as MicroserviceError
          err.status = 499
          err.error = 'canceled'
          err.stack = errStack
          throw err
        }

        if (!axios.isAxiosError(e)) {
          this.rogue?.log.error('Failed HTTP request', e, { url, method })
        } else if (!e.request) {
          // Something happened that meant the request couldnt be sent. Bad request body maybe?
          this.rogue?.log.error(`Unable to build Axios request to ${url}`, e)
        } else if (!e.response) {
          message = 'no_response'
          if (this.rogue?.network.isOffline) {
            this.rogue?.log.info('Request failed because browser is offline')
            message = 'browser_offline'
          } else if (url.indexOf('api.rfksrv.com') !== -1 || url.indexOf('uat.rfksrv.com') !== -1) {
            this.rogue?.log.set('tag', { adblock: true })
            this.rogue?.log.debug('Could not load response from RFK')
            // Dont need to log warnings for RFK events (which are often blocked by adblock)
          } else {
            this.rogue?.log.info(`No server response: ${method || 'get'} to ${debugUrl}`, debug)
          }
        } else if (!e.response.data) {
          if (e.response.status !== 499) {
            this.rogue?.log.warn(`Received null response: ${method || 'get'} to ${config.url}`, debug)
          }
        } else {
          // This is a valid Axios response - lets parse the response body to see why it failed
          const response = e.response

          error = response.data.error?.type || response.data.error?.message || 'unknown_error'

          status = response.status

          // List service gets requests without customer data (no session or JWT) - can be fixed by reloading session
          const errorMessage = response.data.error?.message
          const authErrors = ['Missing customer data', 'auth_required']
          if (authErrors.includes(error) || authErrors.includes(errorMessage)) {
            error = 'TOKEN_EXPIRED'
          }

          message = response.data.error?.message || message

          if (!error && (response.headers['content-type'] || '').toLowerCase().indexOf('json') === -1) {
            // Not a json response...
            debug.body = (response.data || '(UNDEFINED RESPONSE)').substring(0, 150)

            if (status === 429 && response.headers.server === 'cloudflare' && this.rogue?.env !== 'prod') {
              // Show rate limited error
              if (this.rogue?.ssr) {
                this.rogue.log.error('Cloudflare rate limiting SSR', e)
              } else {
                if (this.rogue) {
                  const uiStore = useUIStore(this.rogue.pinia)
                  uiStore.showOverlay('cf-modal', this.rogue)
                }
                nextTick(() => {
                  setTimeout(() => writeToCloudflareIframe(response.data), 50)
                })
              }
            }
            this.rogue?.log.warn(`Non-parsable response: ${status} ${method} to ${debugUrl}`, debug)
          } else if (!response.data.error) {
            // This response doesn't follow the normal microservice error format
            // {error: {type: 'some_code', message: 'Some english string', status: number}}
            debug.json = response.data
            this.rogue?.log.warn(`${status} ${method} to ${debugUrl}`, debug)
          }
        }

        const err = new Error(message) as MicroserviceError

        err.status = status
        err.stack = errStack
        err.error = error

        throw err
      })
  },
}
